<template>
  <v-app>
    <div>
      <v-toolbar elevation="0">
        <router-link class="text-decoration-none" to="/settings?tab=user-management">
          <v-btn icon>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </router-link>
        <v-toolbar-title>{{$t('UserDetails.AccessLevel')}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn class="ml-5 red white--text" text @click="deleteUser" :disabled="!canDeleteUser || pageLoading">{{$t('UserDetails.DeleteUser')}}</v-btn>
      </v-toolbar>
      <v-divider></v-divider>
      <Loader v-if="pageLoading" />
      <v-container v-else fluid grid-list-lg>
        <v-layout wrap>
          <v-flex lg4 md4 xs12>
            <v-card :min-height="flexHeight" elevation="12">
              <v-card-title class="pb-5">
                {{$t('GeneralTranslations.Profile')}}
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-img
                    id="avatar"
                    ref="avatar"
                    :src="user.profilePicUrl"
                    :lazy-src="defaultAvatar"
                    height="280"
                    min-height="250"
                  >
                  </v-img>
                </v-row>

                <v-row class="pt-3 px-2">
                  <v-btn icon @click="openGalleryPhotos()">
                    <v-icon class="ignore-readonly" color="#3F51B5">mdi-image-multiple</v-icon>
                  </v-btn>

                  <v-btn
                      icon
                      @click="$refs.inputUpload.click()"
                  >
                    <v-icon color="#3F51B5">mdi-camera</v-icon>
                  </v-btn>

                  <input
                    v-show="false"
                    ref="inputUpload"
                    type="file"
                    @change="saveImage"
                    accept="image/*"
                  />

                  <v-spacer></v-spacer>

                  <div>
                    <v-btn
                      v-if="canEditUserDetails"
                      @click="toggleEditing"
                      color="primary"
                      outlined
                    >
                      {{
                        editing ? $t('GeneralTranslations.Cancel') : $t('GeneralTranslations.Edit') 
                      }}
                    </v-btn>

                    <v-btn
                      v-if="editing"
                      @click="updateUserProfile"
                      color="primary"
                      class="ml-3"
                      :loading="loading"
                    >
                      {{$t('GeneralTranslations.Save')}}</v-btn>
                  </div>
                </v-row>

                <v-row>
                  <v-col cols="4">
                    <b>{{$t('VeeValidate.Fields.Name')}}</b>
                  </v-col>
                  <v-col cols="6">
                    {{ user.name }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="4">
                    <b>{{$t('VeeValidate.Fields.Username')}}</b>
                  </v-col>
                  <v-col
                    cols="6"
                    v-if="editing && isCanEditStaffEmail"
                  >
                    <v-text-field
                      v-model="user.email"
                      @change="onChangeEmail"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" v-else>
                    {{ user.email }}
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="4">
                    <b>{{$t('UserDetails.AccessLevel')}}</b>
                  </v-col>
                  <v-col cols="6" v-if="editing">
                    <v-select :disabled="currentUser.accessLevel !== accessLevelsEnum.OrgAdmin" :items="accessLevels"
                      item-text="text" item-value="value" 
                      v-model="accessLevel">
                    </v-select>
                  </v-col>
                  <v-col cols="6" v-else>
                    {{ accessLevelDisplayName }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="4">
                    <b> {{$t('GeneralTranslations.Role')}}</b>
                  </v-col>
                  <v-col cols="6" v-if="editing && isCanEditStaffRoles">
                    <v-select
                      v-model="user.roles"
                      :items="usersRoles"
                      item-text="name"
                      :label="$t('GeneralTranslations.Role')"
                      multiple
                    ></v-select>
                  </v-col>
                  <v-col cols="6" v-else>
                    {{ user.roles ? user.roles.join(", ") : ""}}
                  </v-col>
                </v-row>

                <v-row v-if="editing && staffFamilyMessagingEnabled && facility.canStaffMessageFamily && isCanEditStaffRoles">
                  <v-col cols="4">
                    <b>{{$t('UserDetails.CanMessageFamily')}}</b>
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="canMessageFamily"
                      :label="$t('UserDetails.CanMessageFamilyDescription')"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <v-row v-else-if="staffFamilyMessagingEnabled && facility.canStaffMessageFamily">
                  <v-col cols="4">
                    <b>{{$t('UserDetails.CanMessageFamily')}}</b>
                  </v-col>
                  <v-col cols="6">
                    {{ canMessageFamily ? $t('GeneralTranslations.Yes') : $t('GeneralTranslations.No') }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-flex>

          <v-flex lg8 md8 xs12>
            <v-card :min-height="flexHeight" elevation="12">
              <v-card-title>{{$t('UserDetails.FacilityAccess')}}</v-card-title>
              <v-container>
                <v-card-text>
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">{{$t('GeneralTranslations.Facility')}}</th>
                          <th class="text-left"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in user.facilities" :key="item.name">
                          <td>
                            <div class="d-flex align-center">
                              {{ item.name }}
                              <v-chip
                                v-if="user.facilityId === item.id"
                                class="ml-2"
                                color="primary"
                                outlined
                              >
                                {{ $t('UserDetails.MainFacility') }}
                              </v-chip>
                              <v-tooltip v-if="user.facilityId === item.id && user.refOrigin === 'rci'" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-chip
                                    class="ml-2"
                                    color="blue darken-3"
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    <v-icon left small>mdi-shield-lock</v-icon>
                                    mcare managed
                                  </v-chip>
                                </template>
                                <span>This facility is managed by mcare and cannot be removed. The user was synced from an external system.</span>
                              </v-tooltip>
                            </div>
                          </td>
                          <td class="text-right">
                            <v-btn
                              class="ml-5 red white--text"
                              text
                              @click="removeFacilityFromUser(item)"
                              :disabled="!canRemoveFacilityAccess || user.facilities.length === 1 || (user.refOrigin === 'rci' && user.facilityId === item.id)"
                            >
                              {{ $t('GeneralTranslations.Remove') }}
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-spacer></v-spacer>
                </v-card-text>
                <v-card-actions class="pt-4">
                  <v-spacer></v-spacer>
                  <v-dialog v-model="dialog" max-width="600px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-if="filteredStates.length > 0" class="ml-5" color="primary" v-bind="attrs" v-on="on" :disabled="isFacilityUser || isHomeAdmin || user.refOrigin === 'rci'">{{$t('UserDetails.AddFacility')}}</v-btn>
                      <v-btn class="ml-5" color="primary" @click="resetPassword" :disabled="!canResetPassword">
                        {{$t('UserDetails.ResetPassword')}}
                      </v-btn>
                    </template>
                    <v-card class="pb-4 px-4">
                      <v-card-title>
                        <span class="headline">{{$t('UserDetails.PickFacility')}}</span>
                      </v-card-title>
                      <v-card-text>
                      <v-select v-model="facilityToAdd" :items="filteredStates" color="primary"
                          :rules="[v => !!v || 'You must select a Facility']" class="px-0 mx-0 pt-3" item-text="name"
                          :label="$t('GeneralTranslations.Facility')" required return-object></v-select>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn color="primary" @click="addFacilityToUser">{{$t('UserDetails.AddFacility')}}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-card-actions>
              </v-container>
            </v-card>
          </v-flex>

          <v-flex lg12 md12 xs12>
            <StaffNotificationActivity
              :userId="user.id"
              :userEmail="user.email"
              :facilitiesIds="user.facilitiesIds || [user.facilityId]"
            />
          </v-flex>
        </v-layout>
      </v-container>
    </div>
    <ConfirmDlg ref="confirmDelete" />

    <ConfirmMessageDlg ref="confirmResetPassword" />

    <GalleryPhotosUploadDlg
      ref="GalleryPhotosUploadDlg"
      @uploadGalleryPhoto="uploadGalleryPhoto"
    />

    <v-dialog v-model="changeMainFacilityDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{$t('UserDetails.ChangeMainFacility')}}</span>
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            {{ changeMainFacilityMessage }}
          </p>
          <v-select
            v-model="newMainFacility"
            :items="availableFacilitiesForMain"
            :label="$t('UserDetails.SelectNewMainFacility')"
            item-text="name"
            return-object
            outlined
            dense
            :rules="[v => !!v || $t('UserDetails.FacilityRequired')]"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelChangeMainFacility" :disabled="changingMainFacility">
            {{$t('GeneralTranslations.Cancel')}}
          </v-btn>
          <v-btn color="primary" :disabled="!newMainFacility" @click="confirmChangeMainFacility" :loading="changingMainFacility">
            {{$t('GeneralTranslations.Confirm')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<style scoped>
.v-list {
  height: 300px;
  overflow-y: auto;
}

.datepickers {
  padding: 0px !important;
}
.v-btn.v-btn--disabled {
  background-color: rgba(0, 0, 0, 0.12) !important;
}
</style>
<script>
import { mapState } from "vuex";
import { StaffAccessLevels } from "@/enums/StaffAccessLevels";
import firebase from "firebase";
import { getRegionName } from "@/utils/functionsRegion";
import {v4 as uuidv4} from "uuid";
import { optimizePhotos } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import {waitResizedImage} from "@/utils/wait-resized-image";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";
import { getStaffAccessLevels, getAccessLevelDisplayName } from "@/utils/accessControl/getAccessLevelDisplayName";
import { DEFAULT_AVATAR_BLACK } from '@/consts/default-avatar';
import Loader from '@/components/loader/Loader.vue';
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { FeatureFlags, isFeatureEnabled } from '@/consts/featureFlags';

export default {
  name: "UserDetails",
  components: {
    Loader,
    ConfirmMessageDlg: () => import("@/components/popup/confirm-message-dialog/ConfirmMessageDlg.vue"),
    GalleryPhotosUploadDlg: () => import("@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue"),
    ConfirmDlg: () => import("@/components/popup/confirm-dialog/ConfirmDlg.vue"),
    StaffNotificationActivity: () => import("@/components/staff/StaffNotificationActivity.vue"),
  },
  data: () => ({
    flexHeight: 410,
    facilityToAdd: null,
    dialog: false,
    editing: false,
    accessLevelsEnum: StaffAccessLevels,
    accessLevel: "",
    loading: false,
    pageLoading: true,
    isChangedRole: false,
    isChangedEmail: false,
    defaultAvatar: DEFAULT_AVATAR_BLACK,
    changeMainFacilityDialog: false,
    changeMainFacilityMessage: '',
    newMainFacility: null,
    availableFacilitiesForMain: [],
    facilityToRemove: null,
    changingMainFacility: false
  }),

  computed: {
    ...mapState({
      user: state => state.userModule.selecteduser,
      facilitiesForOrg: state => state.userModule.facilitiesForOrg,
      currentUser: state => state.authModule.user
    }),
    usersRoles() {
      return this.$store.state.userModule.usersRoles;
    },
    isFacilityUser() {
      return this.currentUser.accessLevel === this.accessLevelsEnum.FacilityUser;
    },
    isHomeAdmin() {
      return this.currentUser.accessLevel === this.accessLevelsEnum.HomeAdmin;
    },
    currentFacility() {
      return this.$store.state.authModule.currentFacility;
    },
    filteredStates() {
      return this.$store.state.userModule.facilitiesForOrg?.filter(facility => !this.$store.state.userModule.selecteduser?.facilitiesIds?.includes(facility.id));
    },
    facility() {
      return this.$store.state.authModule.currentFacilityData;
    },
    accessLevelDisplayName(){
      return getAccessLevelDisplayName(this.user.accessLevel)
    },
    canEditUserDetails() {
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Settings_UserProfile_EditProfile)
    },
    canRemoveFacilityAccess() {
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Settings_UserDetails_RemoveFacilityAccess)
    },
    canDeleteUser() {
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Settings_UserDetails_DeleteUser)
    },
    canResetPassword(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Settings_UserDetails_ResetPassword)
    },
    accessLevels(){
        const hasMaintenanceFeature = this.$store.state.authModule.currentFacilityData.featureMaintenance;
        return getStaffAccessLevels(false, hasMaintenanceFeature);
    },
    isCanEditStaffEmail() {
      return !this.facility?.isEnabledStaffAPI && [this.accessLevelsEnum.OrgAdmin, this.accessLevelsEnum.HomeAdmin].includes(this.currentUser.accessLevel);
    },
    isCanEditStaffRoles() {
      return [this.accessLevelsEnum.OrgAdmin, this.accessLevelsEnum.HomeAdmin].includes(this.currentUser.accessLevel);
    },
    canMessageFamily: {
      get() {
        if (this.user?.canMessageFamily !== undefined) {
          return this.user.canMessageFamily;
        }
        const allowedLevels = this.facility?.familyMessagingAccessLevels || [];
        return allowedLevels.length === 0 || allowedLevels.includes(this.user?.accessLevel);
      },
      set(value) {
        this.user.canMessageFamily = value;
      }
    },
    staffFamilyMessagingEnabled() {
      return isFeatureEnabled(this.facility, FeatureFlags.staffFamilyMessagingEnabled);
    }
  },
  async created() {
    this.pageLoading = true
    await this.$store.dispatch("getSelectedUser", this.$route.params.id);
    this.pageLoading = false
    this.$store.dispatch("getFacilitiesForOrg");
    this.$store.dispatch('getUsersRoles');
    this.$store.dispatch("getMaintenanceCategories");
    this.$store.dispatch("getCareAPIKey", this.facility.id);
  },
  watch: {
    user(user) {
      if (user) {
        this.accessLevel = this.user.accessLevel;
      }
    },
    "user.roles"() {
      this.isChangedRole = true;
    }
  },
  methods: {
    async removeFacilityFromUser(facility) {
      // Check if trying to remove the main facility (facilityId)
      if (this.user.facilityId === facility.id) {
        // Get available facilities (excluding the one being removed)
        const availableFacilities = this.user.facilities.filter(f => f.id !== facility.id);
        
        if (availableFacilities.length === 0) {
          // This shouldn't happen due to the button being disabled, but just in case
          this.$toast.open({
            message: this.$t('UserDetails.CannotRemoveLastFacility'),
            type: 'error'
          });
          return;
        }

        // Show modal to select new main facility
        this.changeMainFacilityMessage = this.$t('UserDetails.MainFacilityRemovalMessage', { 
          facilityName: facility.name 
        });
        this.availableFacilitiesForMain = availableFacilities;
        this.newMainFacility = null;
        this.facilityToRemove = facility;
        this.changeMainFacilityDialog = true;
      } else {
        // Not removing main facility, proceed normally
        const facilities = this.user.facilities.filter(function (el) {
          return el.id != facility.id;
        });
        const facilitiesIds = this.user.facilitiesIds.filter(function (el) {
          return el != facility.id;
        });

        this.user.facilities = facilities;
        this.user.facilitiesIds = facilitiesIds;
        
        this.$store.dispatch("updateUser", this.user);
        this.$toast.open({
          message: this.$t('UserDetails.FacilityRemoved'),
          type: 'success'
        });
      }
    },
    
    cancelChangeMainFacility() {
      this.changeMainFacilityDialog = false;
      this.newMainFacility = null;
      this.facilityToRemove = null;
    },
    
    async confirmChangeMainFacility() {
      if (!this.newMainFacility) return;
      
      this.changingMainFacility = true;
      
      const facility = this.facilityToRemove;
      
      // Update the main facilityId to the newly selected one
      this.user.facilityId = this.newMainFacility.id;
        
      
      // Now proceed with removal
      const facilities = this.user.facilities.filter(function (el) {
        return el.id != facility.id;
      });
      const facilitiesIds = this.user.facilitiesIds.filter(function (el) {
        return el != facility.id;
      });

      this.user.facilities = facilities;
      this.user.facilitiesIds = facilitiesIds;

      // Change user facility in the backend
      try {
        const result = await this.$store.dispatch("changeUserFacility", { 
          userId: this.user.id, 
          facilityId: this.user.facilityId 
        });
        
        console.log('changeUserFacility result:', result);
        
        // Update the user in Firestore
        await this.$store.dispatch("updateUser", this.user);
        
        // Refresh the selected user to update computed properties
        await this.$store.dispatch("getSelectedUser", this.user.id);
        
        this.changingMainFacility = false;
        this.changeMainFacilityDialog = false;
        this.newMainFacility = null;
        this.facilityToRemove = null;
        
        this.$toast.open({
          message: this.$t('UserDetails.FacilityRemovedAndMainChanged'),
          type: 'success'
        });
      } catch (error) {
        console.error('Unable to update user facility:', error);
        this.changingMainFacility = false;
        this.$toast.open({
          message: this.$t('GeneralTranslations.UnhandledError'),
          type: 'error'
        });
      }
    },
    
    addFacilityToUser() {
      const facilityIdToAdd = this.facilityToAdd.id.toString()
      this.user.facilities.push({
        id: facilityIdToAdd,
        name: this.facilityToAdd.name
      });
      this.user.facilitiesIds.push(facilityIdToAdd);
      this.$store.dispatch("updateUser", this.user);
      this.facilityToAdd = null;
      this.dialog = false;
    },

    deleteUser() {
      try {
        this.$refs.confirmDelete.open(
          this.$i18n.messages[this.$i18n.locale]['UserDetails.DeleteUser'],
          this.$i18n.messages[this.$i18n.locale]['UserDetails.DeleteUserMessage'],
          false,
          this.$i18n.messages[this.$i18n.locale]['UserDetails.ReasonSharedWithVisitor']
        )
          .then(response => {
            if (response && response.agree) {
              this.$store.dispatch("deleteUser", this.user.id).then(() => {
                this.$router.push("/settings?tab=user-management");
              });
            }
          });

      } catch (error) {
        console.error(error);
      }
    },

    toggleEditing() {
      this.editing = !this.editing;
    },

    async updateUserProfile() {
      this.loading = true;
      const roles = [];
      const rolesIds = [];

      if (this.isChangedRole) {
        for (const roleName of this.user.roles) {
          const role = this.usersRoles.find(x => x.name === roleName);
          if (role) {
            roles.push(role.name);
            rolesIds.push(role.id);
          }
        }
      }

      let email = null;
      if(this.isChangedEmail) {
        const existUser = await firebase
          .firestore()
          .collection("users")
          .where("orgId", "==", this.facility.orgId)
          .where("facilitiesIds", 'array-contains', this.facility.id)
          .where("email", "==", this.user.email)
          .get()

        if(!existUser.empty) {
          this.$toast.open({
            message: this.$t("GeneralTranslations.EmailAlreadyExists"),
            type: "error"
          });

          this.loading = false;
          return;
        }

        const result = await firebase
          .app()
          .functions(getRegionName())
          .httpsCallable("staff-updateStaffUserEmail")({
            id: this.user.id,
            email: this.user.email,
          });

        if (result?.data?.success) {
          email = this.user.email;
        } else {
          console.error(result.data.message);

          this.$toast.open({
            message: this.$t("GeneralTranslations.UnhandledError"),
            type: "error"
          });

          this.loading = false;
          return;
        }
      }

      await this.$store.dispatch("updateUserProfile", {
        ...this.user,
        accessLevel: this.accessLevel,
        roles: this.isChangedRole ? roles : undefined,
        rolesIds: this.isChangedRole ? rolesIds : undefined,
        email: email
      });

      this.editing = false;
      this.loading = false;
      this.isChangedRole = false;
      this.isChangedEmail = false;

      this.$toast.open({
        message: this.$t("GeneralTranslations.UserUpdated"),
        type: "success"
      });
    },

    resetPassword() {
      (this.$refs.confirmResetPassword)
        .open(null, this.$i18n.messages[this.$i18n.locale]['UserDetails.ResetPasswordEmailQuestion'], false, "", null, 
        this.$i18n.messages[this.$i18n.locale]['UserDetails.PasswordHasBeenResetMessage'])
        .then(response => {
          if (response && response.agree) {
            firebase.app().functions(getRegionName()).httpsCallable("resetUserPassword")({
              userId: this.$route.params.id,
              initiatorAccessLevel: this.currentUser.accessLevel,
              initiatorName: this.$store.state.authModule.user.name,
              typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
            })
          }
        });
    },

    openGalleryPhotos() {
      this.$refs.GalleryPhotosUploadDlg.open();
    },

    uploadGalleryPhoto(image) {
      this.user.profilePicUrl = image.image;
      this.user.thumbnailUrl = image?.thumbnailUrl ?? image.image;

      this.$store.dispatch("updateUserPhoto", {
        ...this.user,
        profilePicUrl: image.image,
        thumbnailUrl: image?.thumbnailUrl ?? image.image
      });
    },

    async saveImage(event) {
      const ref = uuidv4();
      const file = await optimizePhotos(event.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        return;
      }

      const storageRef = firebase
        .storage()
        .ref()
        .child(this.currentFacility)
        .child("staff")
        .child(this.user.id.toString())
        .child("profilePic")
        .child(ref)
        .put(file);

      const path = (await storageRef)?.metadata?.fullPath
      const thumbnailImageRef = firebase.storage().ref().child(`${path}_200x200`);

      storageRef.on(
          `state_changed`,
          snapshot => {
            console.log("Uploading...")
          },
          error => {
            console.error(error.message);
          },
          () => {
            storageRef.snapshot.ref.getDownloadURL().then(async url => {
              const thumbnailUrl = await waitResizedImage(10, thumbnailImageRef);
              firebase
                  .firestore()
                  .collection("users")
                  .doc(this.user.id)
                  .update({
                    profilePicUrl: url,
                    thumbnailUrl: thumbnailUrl,
                    updatedById: this.user.id,
                    updatedByType: UserTypeEnum.Staff,
                    typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
                    initiatorAccessLevel: this.currentUser.accessLevel,
                    initiatorName: this.$store.state.authModule.user.name
                  })
                  .then(async () => {
                    this.user.profilePicUrl = url;
                    await this.$store.dispatch("getUser");
                  });
            });
          }
      );

    },

    onChangeEmail() {
      this.isChangedEmail = true;
    },
  }
};
</script>
<style lang="scss" scoped>
  .v-list {
    height: auto;
  }
</style>
